import React, { useState, useEffect, useRef } from "react";
import { CircleFlag } from "react-circle-flags";
import { useNavigate } from "react-router-dom";
import { countryData } from "./country-data";
import {
  useStaggeredAnimation,
  generateAnimationDelays,
} from "utils/hooks/use-staggered-animation";

import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";

const ANIMATION_DELAYS = generateAnimationDelays(6);

interface Country {
  name: string;
  iso: string;
  minutes: string;
  sms: string;
  rate?: string;
}

export const FreeInternationalCallsHero = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>("Afghanistan");
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    countryData[0]
  );
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { sectionRef, animatedStates } = useStaggeredAnimation({
    delays: ANIMATION_DELAYS,
    threshold: 0.1,
  });

  const [
    imgAnimated,
    h1Animated,
    pAnimated,
    searchAnimated,
    statsAnimated,
    buttonAnimated,
  ] = animatedStates;

  // Filter countries based on search input
  const filteredCountries: Country[] = React.useMemo(() => {
    if (!searchValue.trim()) {
      return countryData.slice(0, 25);
    }
    return countryData.filter((country: Country) =>
      country.name.toLowerCase().includes(searchValue.toLowerCase().trim())
    );
  }, [searchValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setSearchValue(value);
    setShowDropdown(true);
    setActiveIndex(-1);
  };

  const handleInputFocus = (): void => {
    setShowDropdown(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showDropdown) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          Math.min(prev + 1, filteredCountries.length - 1)
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && filteredCountries[activeIndex]) {
          handleCountrySelect(filteredCountries[activeIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setActiveIndex(-1);
        break;
    }
  };

  const handleCountrySelect = (country: Country): void => {
    setSelectedCountry(country);
    setSearchValue(country.name);
    setShowDropdown(false);
  };

  const handleCheckDestination = (): void => {
    const matchingCountry = countryData.find(
      (country: Country) =>
        country.name.toLowerCase() === searchValue.toLowerCase().trim()
    );

    if (matchingCountry) {
      handleCountrySelect(matchingCountry);
    } else if (filteredCountries.length > 0) {
      handleCountrySelect(filteredCountries[0]);
    } else {
      setShowDropdown(true);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative z-0 xl:px-12 pt-8 sm:pt-[3.25rem] md:pt-[4.5rem] lg:pt-[5.875rem] pb-9 sm:pb-14 md:pb-[5.5rem] lg:pb-[7.5rem]"
    >
      <div className="absolute -z-[1] bg-[#FE4D0114] top-0 left-1/2 [@media(min-width:480px)]:left-[40%] sm:!left-[60%] right-0 bottom-9 sm:bottom-14 md:bottom-[5.5rem] lg:bottom-[7.5rem]" />
      <div className="flex flex-col xl:flex-row justify-center xl:justify-start items-center xl:gap-24 2xl:gap-[7.5rem] w-full max-w-[90rem] mx-auto">
        <div className="self-start px-5 w-full xl:order-2 sm:self-auto sm:px-6 md:px-7 xl:px-0">
          <div
            className={`w-full [@media(max-width:400px)]:max-w-60 max-w-[18.75rem] sm:max-w-80 lg:max-w-[21.62444rem] sm:mx-auto xl:mx-0 transition-all duration-1000 ease-out ${
              imgAnimated
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <div className="w-full aspect-[0.6564/1] overflow-hidden">
              <img
                src="/assets/international-calls-iphone-mockup.webp"
                alt="iPhone 12 Pro Mockup"
                className="object-cover size-full"
              />
            </div>
          </div>
        </div>
        <div className="xl:order-1 relative z-[1] bg-white w-full px-5 sm:px-6 md:px-7 lg:px-9 xl:px-0">
          <div className="w-full xl:max-w-[39.625rem] pt-6 sm:pt-10 md:pt-14 lg:pt-16 xl:pt-0">
            <div className="mb-4 sm:mb-5 md:mb-6 lg:mb-[2.19rem]">
              <h1
                className={`text-[#101828] font-mobile text-[clamp(1.75rem,calc(3.25vw+1rem),4rem)] font-medium leading-tight md:leading-[1.3] lg:leading-[4.5rem] mb-2 sm:mb-3 lg:mb-4 transition-all duration-1000 ease-out ${
                  h1Animated
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                }`}
              >
                Free International Calls to 170+ countries
              </h1>
              <p
                className={`text-[#667085] text-sm sm:text-base md:text-[1.15rem] lg:text-xl font-normal leading-[1.0625rem] sm:leading-5 md:leading-6 lg:leading-7 2xl:leading-[1.875rem] transition-all duration-1000 ease-out ${
                  pAnimated
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                }`}
              >
                Use your VLE mobile minutes to call abroad*
              </p>
            </div>
            <div
              className={`relative z-20 flex gap-3 items-center mb-[1.375rem] sm:mb-6 lg:mb-[1.625rem] transition-all duration-1000 ease-out ${
                searchAnimated
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <div
                className="relative w-full max-w-[24.57753rem]"
                ref={dropdownRef}
              >
                <Input
                  ref={inputRef}
                  id="country"
                  type="text"
                  value={searchValue}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={handleKeyDown}
                  className="bg-white w-full h-10 md:h-[2.875rem] lg:h-12 pl-10 pr-6 py-2 md:pl-[2.875rem] md:pr-8 md:py-3 lg:pl-[3.125rem] lg:pr-9 border-[#D0D5DD] rounded-lg text-black placeholder:text-black/50 text-sm md:text-[0.925rem] lg:text-base font-medium leading-5 md:leading-6 lg:leading-7 focus-visible:outline focus:outline-[#707C8F]"
                  placeholder="Search Destinations..."
                />
                <div className="absolute left-2 top-1/2 -translate-y-1/2 md:left-3">
                  <CircleFlag
                    countryCode={selectedCountry.iso}
                    className="size-[1.625rem] md:size-7 lg:size-[1.875rem]"
                  />
                </div>
                {searchValue && showDropdown && (
                  <div className="absolute top-1/2 -translate-y-1/2 right-2 md:right-3">
                    <img
                      src="/assets/close.svg"
                      alt="Search"
                      className="size-3.5 sm:size-4 lg:size-5 cursor-pointer opacity-50 hover:opacity-100"
                      onClick={() => setSearchValue("")}
                    />
                  </div>
                )}
                {showDropdown && (
                  <div className="absolute top-full inset-x-0 max-h-52 sm:max-h-56 md:max-h-60 lg:max-h-64 bg-white border border-[#D0D5DD] rounded-lg mt-1 sm:mt-1.5 shadow-lg z-10 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-[#D0D5DD] [&::-webkit-scrollbar-thumb]:rounded-full">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country, index) => (
                        <div
                          key={`country-${index}-${country.name
                            .replace(/\s+/g, "-")
                            .toLowerCase()}`}
                          className={`flex items-center gap-2 sm:gap-2.5 lg:gap-3 px-2 py-1.5 sm:px-2.5 sm:py-2 lg:px-3 cursor-pointer border-b border-[#D0D5DD66] last:border-b-0 ${
                            index === activeIndex
                              ? "bg-[#FE4D011A] hover:bg-[#FE4D011A]"
                              : "hover:bg-[#FE4D010D]"
                          }`}
                          onClick={() => handleCountrySelect(country)}
                        >
                          <CircleFlag
                            countryCode={country.iso}
                            className="size-6 sm:size-[1.625rem] lg:size-7"
                          />
                          <span className="inline-block text-[#1D1D1B] text-[0.825rem] sm:text-sm lg:text-[0.925rem] font-medium leading-[1.125rem] sm:leading-5 lg:leading-[1.375rem]">
                            {country.name}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 sm:p-2.5 lg:p-3 text-center text-[#667085] text-[0.825rem] sm:text-sm lg:text-[0.925rem] font-normal leading-[1.125rem] sm:leading-5 lg:leading-[1.375rem]">
                        No destinations found for "{searchValue}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="primary"
                className="w-fit px-4 md:px-[1.125rem] lg:px-5 h-10 md:h-[2.875rem] lg:h-12 rounded-md"
                onClick={handleCheckDestination}
              >
                See rates for
              </Button>
            </div>
            <div
              className={`flex mb-4 sm:mb-5 lg:mb-6 transition-all duration-1000 ease-out ${
                statsAnimated
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <div className="pr-4 sm:pr-5 md:pr-6 lg:pr-7 xl:pr-8 2xl:pr-[2.19rem]">
                <h5 className="font-mobile text-black [@media(max-width:400px)]:text-[0.8125rem] text-[0.925rem] sm:text-base md:text-lg lg:text-xl font-medium [@media(max-width:400px)]:leading-[0.85rem] leading-4 sm:leading-5 md:leading-6 lg:leading-7 [@media(max-width:400px)]:mb-1 mb-1.5">
                  Location
                </h5>
                <p className="text-black [@media(max-width:400px)]:text-sm text-base sm:text-lg md:text-xl lg:text-2xl font-semibold [@media(max-width:400px)]:leading-[1.125rem] leading-5 sm:leading-6 md:leading-7 lg:leading-8">
                  {/* {selectedCountry.rate || '16.50¢ /min'} */}
                  {/* [@media(max-width:400px)]:leading-[0.85rem] leading-4 sm:leading-5 md:leading-6 lg:leading-7 */}
                  {selectedCountry.name}
                </p>
              </div>
              <div className="relative px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 2xl:px-[2.19rem] before:absolute before:top-[17.5%] before:left-0 before:bottom-[20%] before:border-l before:border-[#C0C0C0]">
                <h5 className="font-mobile text-black [@media(max-width:400px)]:text-[0.8125rem] text-[0.925rem] sm:text-base md:text-lg lg:text-xl font-medium [@media(max-width:400px)]:leading-[0.85rem] leading-4 sm:leading-5 md:leading-6 lg:leading-7 [@media(max-width:400px)]:mb-1 mb-1.5 whitespace-nowrap">
                  Included Minutes
                </h5>
                <p className="text-black [@media(max-width:400px)]:text-sm text-base sm:text-lg md:text-xl lg:text-2xl font-semibold [@media(max-width:400px)]:leading-[1.125rem] leading-5 sm:leading-6 md:leading-7 lg:leading-8">
                  {parseFloat(selectedCountry.minutes)
                    ? selectedCountry.minutes + " min"
                    : "Unlimited"}
                </p>
              </div>
              <div className="relative pl-4 sm:pl-5 md:pl-6 lg:pl-7 xl:pl-8 2xl:pl-[2.19rem] before:absolute before:top-[17.5%] before:left-0 before:bottom-[20%] before:border-l before:border-[#C0C0C0]">
                <h5 className="font-mobile text-black [@media(max-width:400px)]:text-[0.8125rem] text-[0.925rem] sm:text-base md:text-lg lg:text-xl font-medium [@media(max-width:400px)]:leading-[0.85rem] leading-4 sm:leading-5 md:leading-6 lg:leading-7 [@media(max-width:400px)]:mb-1 mb-1.5">
                  Text SMS
                </h5>
                <p className="text-black [@media(max-width:400px)]:text-sm text-base sm:text-lg md:text-xl lg:text-2xl font-semibold [@media(max-width:400px)]:leading-[1.125rem] leading-5 sm:leading-6 md:leading-7 lg:leading-8">
                  {selectedCountry.sms}
                </p>
              </div>
            </div>
            <div
              className={`transition-all duration-1000 ease-out ${
                buttonAnimated
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <Button
                type="button"
                variant="link"
                className="w-fit text-[#FE4D01] hover:text-[#E44501] [@media(max-width:400px)]:text-[0.8125rem] text-[0.925rem] sm:text-base md:text-lg lg:text-xl font-semibold [@media(max-width:400px)]:leading-[0.85rem] leading-4 sm:leading-5 md:leading-6 lg:leading-7"
                onClick={() => navigate("/network-plan")}
              >
                Choose your plan »
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
